import { Component, OnDestroy, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { selectInfo, selectNraIndicator, selectStateCode } from 'projects/money-movement-ui/src/app/account-info-header/reducers';
import { amountTypeDict, Constants, frequencyDict, transactionDetailLabels, transactionDirection, transactionTypes, transactionTypesDict } from 'projects/money-movement-ui/src/app/shared/utilities/Constants';
import { selectIsStandingAuthViewPage, selectIsTransactionsViewPage, selectViewFrequency } from 'projects/money-movement-ui/src/app/transaction-view/reducers';
import { combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { isAchForm } from '../../../../../create-request/models/form/TransactionForm';
import {
  selectAchInfo,
  selectAchInfoToFromAccount,
  selectMoneyLinkType,
  selectStagedStandingAuthid,
  selectStagedTransactionForm
} from '../../../../../create-request/reducers';
import { FeatureFlagService } from '../../../../services/feature-flag-service';

@Component({
  standalone: false,
  selector: 'mm-non-regular-transaction-details-section-ach',
  templateUrl: './non-regular-transaction-details-section-ach.component.html',
  styleUrls: ['./non-regular-transaction-details-section-ach.component.scss']
})
export class NonRegularTransactionDetailsSectionAchComponent implements OnInit, OnDestroy {
  readonly labels = transactionDetailLabels;
  readonly frequencyTypes = frequencyDict;
  transactionTypeLabel: string = transactionTypesDict[transactionTypes.ach];
  transferDirection: string;
  nextWithdrawalDate: string;
  nextCompletionDate: string;
  withdrawalDateCopy: string = Constants.expectedWithdrawalDateCopy;
  fundsAvailableToTrade: string;
  fundsAvailableToTradeCopy: string = Constants.fundsAvailableToTrade;
  amount: string;
  amountType: string;
  isIra: boolean;
  isPtrs: boolean;
  contributionReason: string;
  distributionReason: string;
  selectedAuthorization: string;
  nickname: string;
  to1: string;
  to2: string;
  fedTaxPercent: string;
  fedTaxAmount: string;
  stateTaxPercent: string;
  stateTaxAmount: string;
  grossAmount: string;
  netAmount: string;
  trustIncomePercent: string | undefined;
  trustPrincipalPercent: string | undefined;
  trustTaxCode: string | undefined;
  trustPaidFor: string | undefined;
  trustNetTransferAmount: string | undefined;
  trustComment: string | undefined;
  frequency: string | undefined;
  destroy$: Subject<boolean> = new Subject<boolean>();
  isStandingAuthViewPage: boolean = false;
  isTransactionViewPage: boolean = false;
  secondCompletionDate: string;
  endDate: string;
  maxTransferAmount: string;
  viewPagePtrsInfoBannerPrimaryText = Constants.viewPagePtrsInfoBannerPrimaryText;
  viewPagePtrsInfoBannerBoldText = Constants.viewPagePtrsInfoBannerBoldText;
  isThirdParty: boolean = false;
  transactionType: string;
  moneyLinkType: string;
  accountName: string;
  accountType: string;
  abaNumber: string;
  stateTaxLabel: string = this.labels.stateTax;

  constructor(private store: Store, private featureFlagService: FeatureFlagService) {
  }

  ngOnInit(): void {
    combineLatest([
      this.store.pipe(select(selectStagedTransactionForm)),
      this.store.pipe(select(selectAchInfo))
    ]).pipe(takeUntil(this.destroy$),
    ).subscribe(([form, achInfo]) => {
      if (form?.transactionType && isAchForm(form)) {
        const extraPrompts = form.extraPrompts;
        const transactionDetails = form.transactionDetails;
        this.transferDirection = extraPrompts.transferDirection;
        this.nextWithdrawalDate = transactionDetails.withdrawalDate;
        this.nextCompletionDate = transactionDetails.completionDate;
        this.fundsAvailableToTrade = transactionDetails.fundsAvailableToTrade;
        this.contributionReason = transactionDetails.contributionReason;
        this.trustIncomePercent = transactionDetails.trustIncomePercent;
        this.trustPrincipalPercent = transactionDetails.trustPrincipalPercent;
        this.trustTaxCode = transactionDetails.trustTaxCode;
        this.trustPaidFor = transactionDetails.trustPaidFor;
        this.trustComment = transactionDetails.trustComment;
        this.fedTaxPercent = form.transactionDetails.taxWithholdingsFormGroup?.fedTaxPercent ?? '';
        this.stateTaxPercent = form.transactionDetails.taxWithholdingsFormGroup?.stateTaxPercent ?? '';
        this.amountType = transactionDetails.amountType === amountTypeDict.net ? 'Net' : 'Gross';
        this.amount = achInfo.withdrawalAmount;
        this.validateDates();
      }
    });

    combineLatest([
      this.store.select(selectAchInfoToFromAccount),
      this.store.select(selectViewFrequency),
      this.store.select(selectAchInfo)
    ])
      .pipe(
        takeUntil(this.destroy$))
      .subscribe(([toFromAccount, frequency, achInfo]) => {
        this.nickname = achInfo.nickName;
        this.to1 = toFromAccount.financialInstitution;
        this.accountName = `Account name:  ${toFromAccount.accountName}`
        this.to2 = `Account number: ${toFromAccount.accountNumber}`;
        this.accountType = `Account type: ${toFromAccount.accountType}`;
        this.abaNumber = `ABA number: ${toFromAccount.abaNumber}`;
        this.frequency = frequency;
      });

    this.store.select(selectInfo).pipe(takeUntil(this.destroy$)).subscribe(info => {
      this.isIra = info.classifications.isIra;
      this.isPtrs = info.classifications.isPtrs;
    });

    this.store.select(selectStagedStandingAuthid).pipe(takeUntil(this.destroy$)).subscribe(selectedAuthorization => {
      this.selectedAuthorization = selectedAuthorization?.split('-')[1];
    });

    this.store
      .select(selectIsStandingAuthViewPage)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isStandingAuthViewPage => this.isStandingAuthViewPage = isStandingAuthViewPage);

    this.store
      .select(selectIsTransactionsViewPage)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isTransactionViewPage => this.isTransactionViewPage = isTransactionViewPage);

    this.store
      .select(selectMoneyLinkType)
      .pipe(takeUntil(this.destroy$))
      .subscribe(moneyLinkType => {
        this.moneyLinkType = moneyLinkType;
        let lastIndex = this.moneyLinkType.lastIndexOf('-')
        this.transactionType = this.transactionTypeLabel + ' ' + this.moneyLinkType.slice(lastIndex)
    });

    this.store.select(selectAchInfo).pipe(takeUntil(this.destroy$)).subscribe(info => {
      this.fedTaxAmount = info.fedTaxAmount?.split(' ')[0];
      this.stateTaxAmount = info.stateTaxAmount?.split(' ')[0];
      this.grossAmount = info.withdrawalAmount;
      this.netAmount = info.transferAmount;
      this.distributionReason = info.distributionReason;
      this.trustNetTransferAmount = info.transferAmount;
      this.secondCompletionDate = info.frequency === '2 Times/Month' ? info.secondProcessDate : '';
      this.endDate = info.endDate;
      this.maxTransferAmount = info.maxTransferAmount;
    });

    combineLatest([
      this.store.select(selectStateCode),
      this.store.select(selectNraIndicator)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([stateCode, nraIndicator]) => {
      if (stateCode && !nraIndicator) {
        this.stateTaxLabel = `${this.labels.stateTax} (${stateCode})`;
      }
    });
  }


  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  //Removes default dates return by CMT when data is not available so that N/A logic in HTML is triggered
  validateDates(): void {
    if (this.nextCompletionDate?.match("0001-01-01")) {this.nextCompletionDate = ''};
    if (this.nextWithdrawalDate?.match("12-31-9999")) {this.nextWithdrawalDate = ''};
  }

  get pageHeader(): string {
    return this.isStandingAuthViewPage ? 'Standing authorization details' : 'Transaction details';
  }

  get isRecurring() {
    return this.frequency && this.frequency !== 'On-Request';
  }

  get isAchIn() {
    return this.transferDirection === transactionDirection.in.toLowerCase()
  }

  isNotEmptyOrNull(param: any) {
    const params = param?.trim();
    if (params === null || params === undefined || params === 'undefined%' || params === '') {
      return false;
    }
    return true;
  }
}
